import React from "react";
import { Box } from "@mui/material";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import wizardStyle from "@/src/components/Form/wizardStyle";
import { useForm } from "@/src/provider/Form/FormProvider";
import FormFader from "@/src/components/Form/Common/Fader/FormFader";
import FormLegend from "@/src/components/Form/Common/FormLegend/FormLegend";
import { translate } from "@/src/services/translations/translations";
import { requiredValidator } from "@/src/services/validationRules";
import FormSubmit from "@/src/components/Form/Common/FormSubmit/FormSubmit";
import ToolsIcon from "@/src/assets/icons/contract/toggleable/IconTools";
import RentIncreaseIcon from "@/src/assets/icons/provider/toggleable/IconPriceIncrease";
import UtilitiesStatementIcon from "@/src/assets/icons/provider/toggleable/IconProviderWrongInvoice";
import DepositIcon from "@/src/assets/icons/contract/toggleable/IconMoneyEnforce";
import LandlordHasCancelledIcon from "@/src/assets/icons/provider/toggleable/IconProviderCancellation";
import IllegalContractClauseIcon from "@/src/assets/icons/provision/toggleable/IconDocumentHouseSignDetailed";
import QuestionMarkIcon from "@/src/assets/icons/common/toggleable/IconQuestionmark";
import Group, { GroupProps } from "@/src/components/Wizard/Group";
import RentalContractReasonForDisputeDetails from "./RentalContractReasonForDisputeDetails";
import IconServiceNotAsAgreed from "@/src/assets/icons/provider/toggleable/IconServiceNotAsAgreed";
import IconEnforce from "@/src/assets/icons/contract/toggleable/IconEnforce";
import IconProviderCancellation from "@/src/assets/icons/provider/toggleable/IconProviderCancellation";
import IconInheritance from "@/src/assets/icons/familyLaw/toggleable/IconInheritance";

export default function RentalContractDetails({ submitProps, product }: any) {
  const { values, handleSubmit } = useForm();

  let reasonForDisputeOptions: GroupProps["options"] = [
    {
      labelText: translate(`rentalContract.reasonForDispute.options.rentIncrease.label`, values),
      labelIcon: <RentIncreaseIcon />,
      labelIconActive: <RentIncreaseIcon active />,
      value: "rentIncrease",
      optionHintText: translate("rentalContract.reasonForDispute.options.rentIncrease.optionHintText", values),
    },
    {
      labelText: translate(`rentalContract.reasonForDispute.options.utilitiesStatement.label`, values),
      labelIcon: <UtilitiesStatementIcon />,
      labelIconActive: <UtilitiesStatementIcon active />,
      value: "utilitiesStatement",
      optionHintText: translate("rentalContract.reasonForDispute.options.utilitiesStatement.optionHintText", values),
    },
    {
      labelText: translate(`rentalContract.reasonForDispute.options.deposit.label`, values),
      labelIcon: <DepositIcon />,
      labelIconActive: <DepositIcon active />,
      value: "deposit",
      optionHintText: translate("rentalContract.reasonForDispute.options.deposit.optionHintText", values),
    },
  ];

  switch (product.contractPartyType) {
    case "tenant":
      reasonForDisputeOptions.push(
        {
          labelText: translate(`rentalContract.reasonForDispute.options.housingDeficiencies.label`, values),
          labelIcon: <ToolsIcon />,
          labelIconActive: <ToolsIcon active />,
          value: "housingDeficiencies",
        },
        {
          labelText: translate(`rentalContract.reasonForDispute.options.landlordHasCancelled.label`, values),
          labelIcon: <LandlordHasCancelledIcon />,
          labelIconActive: <LandlordHasCancelledIcon active />,
          value: "landlordHasCancelled",
        },
        {
          labelText: translate(`rentalContract.reasonForDispute.options.illegalContractClause.label`, values),
          labelIcon: <IllegalContractClauseIcon />,
          labelIconActive: <IllegalContractClauseIcon active />,
          value: "illegalContractClause",
        }
      );
      break;
    case "landlord":
      reasonForDisputeOptions.push(
        {
          labelText: translate(`rentalContract.reasonForDispute.options.depositPayout.label`, values),
          labelIcon: <IconInheritance />,
          labelIconActive: <IconInheritance active />,
          value: "depositPayout",
          optionHintText: translate("rentalContract.reasonForDispute.options.depositPayout.optionHintText", values),
        },
        {
          labelText: translate(`rentalContract.reasonForDispute.options.rentReduction.label`, values),
          labelIcon: <IconServiceNotAsAgreed />,
          labelIconActive: <IconServiceNotAsAgreed active />,
          value: "rentReduction",
          optionHintText: translate("rentalContract.reasonForDispute.options.rentReduction.optionHintText", values),
        },
        {
          labelText: translate(`rentalContract.reasonForDispute.options.rentLate.label`, values),
          labelIcon: <IconEnforce />,
          labelIconActive: <IconEnforce active />,
          value: "rentLate",
          optionHintText: translate("rentalContract.reasonForDispute.options.rentLate.optionHintText", values),
        },
        {
          labelText: translate(`rentalContract.reasonForDispute.options.terminationRejection.label`, values),
          labelIcon: <IconProviderCancellation />,
          labelIconActive: <IconProviderCancellation active />,
          value: "terminationRejection",
          optionHintText: translate("rentalContract.reasonForDispute.options.terminationRejection.optionHintText", values),
        }
      );
      break;
    default:
      break;
  }

  reasonForDisputeOptions.push({
    labelText: translate(`rentalContract.reasonForDispute.options.other.label`, values),
    labelIcon: <QuestionMarkIcon />,
    labelIconActive: <QuestionMarkIcon active />,
    value: "other",
  });

  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={{ ...wizardStyle.applicationForm, ...wizardStyle.wizardForm }} onSubmit={handleSubmit}>
          <Typography variant={"h4"} sx={[wizardStyle.headline, { display: { xs: "block", sm: "none" } }]}>
            Ihr Vertrag
          </Typography>
          <Grid
            container
            sx={{
              justifyContent: "flex-start",
              alignItems: "baseline",
            }}
          >
            <Grid size={12}>
              <FormLegend>
                <strong>Details zu Ihrem Anliegen</strong>
              </FormLegend>
            </Grid>

            <Grid size={12}>
              <Group
                type="radio"
                name="reasonForDispute"
                question={translate(`rentalContract.reasonForDispute.question`, values)}
                options={reasonForDisputeOptions}
                validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
              />
            </Grid>
            <RentalContractReasonForDisputeDetails values={values} />
          </Grid>
          <FormSubmit {...submitProps} />
        </Box>
      </FormFader>
    </Paper>
  );
}
